import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Truck, CreditCard, Clock3 } from "lucide-react";
import { Button } from "../components/ui/button";
import CheckoutSummary from "../components/CheckoutSummary";
import { useApp } from "../context/AppContext";
import { formatRupiah } from "../lib/format";
import SEOHead from "../components/SEOHead";
import { checkoutApi } from "../lib/api";

const COURIERS_PER_PAGE = 5;

const CheckoutPaymentPage = () => {
  const {
    cartItems,
    couriers,
    setCheckoutCouriers,
    selectedCourierId,
    setSelectedCourierId,
    selectedPaymentId,
    setSelectedPaymentId,
    paymentMethods,
    shipping,
    createOrder,
  } = useApp();
  const navigate = useNavigate();
  const [loadingRates, setLoadingRates] = useState(false);
  const [creating, setCreating] = useState(false);
  const [courierPage, setCourierPage] = useState(1);

  const activePaymentMethods = paymentMethods.filter((method) => method.is_active);
  const destination = shipping.subdistrict_id || shipping.district_id;
  const rateDestination = shipping.city_id || shipping.district_id;
  const rateCalculationType = shipping.city_id ? "domestic" : "district";
  const cartSignature = cartItems.map((item) => `${item.productId}:${item.quantity}`).join("|");
  const rateItems = useMemo(
    () =>
      cartSignature
        ? cartSignature.split("|").map((row) => {
            const [productId, quantity] = row.split(":");
            return {
              product_id: Number(productId),
              quantity: Number(quantity),
            };
          })
        : [],
    [cartSignature],
  );

  useEffect(() => {
    if (!rateDestination || cartItems.length === 0) return;

    const loadRates = async () => {
      try {
        setLoadingRates(true);
        const data = await checkoutApi.getShippingRates({
          destination: Number(rateDestination),
          items: rateItems,
          calculation_type: rateCalculationType,
        });
        setCheckoutCouriers(
          (data.rates || []).map((rate) => ({
            ...rate,
            id: `${rate.code}:${rate.service}:${rate.cost}`,
          })),
        );
      } catch (error) {
        toast.error(error.message || "Gagal memuat ongkir dari backend");
      } finally {
        setLoadingRates(false);
      }
    };

    loadRates();
  }, [rateDestination, rateCalculationType, rateItems, cartItems.length, setCheckoutCouriers]);

  const selectedPayment = useMemo(
    () => activePaymentMethods.find((method) => method.code === selectedPaymentId),
    [activePaymentMethods, selectedPaymentId],
  );
  const sortedCouriers = useMemo(
    () =>
      [...couriers].sort((a, b) => {
        if (Number(a.cost || 0) !== Number(b.cost || 0)) {
          return Number(a.cost || 0) - Number(b.cost || 0);
        }
        return `${a.name} ${a.service}`.localeCompare(`${b.name} ${b.service}`);
      }),
    [couriers],
  );
  const totalCourierPages = Math.max(1, Math.ceil(sortedCouriers.length / COURIERS_PER_PAGE));
  const visibleCouriers = sortedCouriers.slice(
    (courierPage - 1) * COURIERS_PER_PAGE,
    courierPage * COURIERS_PER_PAGE,
  );

  useEffect(() => {
    setCourierPage(1);
  }, [rateDestination, rateCalculationType, cartSignature]);

  useEffect(() => {
    if (courierPage > totalCourierPages) {
      setCourierPage(totalCourierPages);
    }
  }, [courierPage, totalCourierPages]);

  if (cartItems.length === 0) {
    return (
      <main className="max-w-3xl mx-auto px-4 py-20 text-center">
        <h1 className="font-display text-2xl font-semibold text-stone-900 mb-3">
          Keranjang Anda kosong
        </h1>
        <Link
          to="/"
          className="inline-flex items-center h-12 px-5 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700"
        >
          Pilih Produk
        </Link>
      </main>
    );
  }

  if (!shipping.name || !shipping.phone || !shipping.address || !destination) {
    return (
      <main className="max-w-3xl mx-auto px-4 py-20 text-center">
        <h1 className="font-display text-2xl font-semibold text-stone-900 mb-3">
          Lengkapi data pengiriman dulu
        </h1>
        <p className="text-stone-600 mb-6">
          Kami butuh alamat lengkap Anda sebelum memilih kurir & metode bayar.
        </p>
        <Link
          to="/checkout/pengiriman"
          className="inline-flex items-center h-12 px-5 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700"
          data-testid="go-shipping-first"
        >
          Isi Data Pengiriman
        </Link>
      </main>
    );
  }

  const onCreate = async () => {
    if (!selectedCourierId) return toast.error("Pilih kurir pengiriman terlebih dahulu");
    if (!selectedPaymentId) return toast.error("Pilih metode pembayaran terlebih dahulu");
    try {
      setCreating(true);
      const invoice = await createOrder({ payment: selectedPayment });
      if (!invoice) {
        toast.error("Gagal membuat pesanan");
        return;
      }
      toast.success("Pesanan berhasil dibuat");
      navigate(`/payment/${invoice}`);
    } catch (error) {
      toast.error(error.message || "Gagal membuat transaksi pembayaran");
    } finally {
      setCreating(false);
    }
  };

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12" data-testid="payment-method-page">
      <SEOHead
        title="Checkout Pembayaran"
        description="Halaman checkout pembayaran Teras Alami untuk memilih kurir dan metode bayar pesanan herbal."
        path="/checkout/pembayaran"
        noIndex
      />

      <Link
        to="/checkout/pengiriman"
        className="inline-flex items-center text-stone-600 hover:text-stone-900 mb-6"
        data-testid="back-shipping"
      >
        <ArrowLeft className="w-4 h-4 mr-2" /> Ubah Data Pengiriman
      </Link>

      <div className="mb-8 flex flex-wrap items-center gap-2 text-sm sm:flex-nowrap sm:gap-3">
        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 font-semibold">
          ?
        </span>
        <span className="font-medium text-stone-900">Data Pengiriman</span>
        <span className="order-3 h-px w-full bg-emerald-200 sm:order-none sm:w-auto sm:flex-1"></span>
        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-emerald-600 text-white font-semibold">
          2
        </span>
        <span className="font-medium text-stone-900">Pengiriman & Pembayaran</span>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 lg:gap-8">
        <div className="lg:col-span-2 space-y-6">
          {/* Courier */}
          <div className="bg-white border border-stone-200 rounded-2xl p-6 sm:p-8 shadow-sm">
            <div className="flex items-center gap-3 mb-5">
              <span className="inline-flex w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 items-center justify-center">
                <Truck className="w-5 h-5" />
              </span>
              <div>
                <h2 className="font-display text-xl sm:text-2xl font-semibold text-stone-900">
                  Pilih Kurir
                </h2>
                <p className="text-stone-500 text-sm">
                  Pilih jasa pengiriman favorit Anda.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3">
              {loadingRates && (
                <div className="sm:col-span-2 rounded-xl border border-stone-200 bg-stone-50 p-5 text-stone-600">
                  Memuat ongkir dari RajaOngkir...
                </div>
              )}
              {!loadingRates && couriers.length === 0 && (
                <div className="sm:col-span-2 rounded-xl border border-dashed border-stone-300 p-5 text-stone-500">
                  Belum ada opsi ongkir untuk alamat ini.
                </div>
              )}
              {visibleCouriers.map((c) => {
                const active = selectedCourierId === c.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setSelectedCourierId(c.id)}
                    className={`text-left p-4 sm:p-5 rounded-xl border transition ${
                      active
                        ? "border-emerald-500 bg-emerald-50 ring-2 ring-emerald-200"
                        : "border-stone-200 bg-white hover:border-stone-300"
                    }`}
                    data-testid={`courier-option-${c.id}`}
                  >
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <p className="font-display text-lg font-semibold text-stone-900">
                          {c.name}
                        </p>
                        <p className="text-sm text-stone-500 flex items-center gap-1 mt-1">
                          <Clock3 className="w-3.5 h-3.5" /> {c.service} {c.etd ? `� Estimasi ${c.etd}` : ""}
                        </p>
                      </div>
                      <span className="font-display text-lg font-semibold text-emerald-700 whitespace-nowrap sm:text-right">
                        {formatRupiah(c.cost)}
                      </span>
                    </div>
                    {active && (
                      <span className="inline-flex mt-3 items-center text-xs font-medium bg-emerald-600 text-white px-2.5 py-1 rounded-full">
                        Dipilih
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            {!loadingRates && sortedCouriers.length > COURIERS_PER_PAGE && (
              <div className="mt-5 flex flex-col gap-3 rounded-xl border border-stone-200 bg-stone-50 p-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-stone-500">
                  Menampilkan {visibleCouriers.length} dari {sortedCouriers.length} opsi termurah - Halaman {courierPage}
                </p>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="h-10 flex-1 rounded-xl sm:flex-none"
                    disabled={courierPage <= 1}
                    onClick={() => setCourierPage((current) => Math.max(1, current - 1))}
                  >
                    Sebelumnya
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="h-10 flex-1 rounded-xl sm:flex-none"
                    disabled={courierPage >= totalCourierPages}
                    onClick={() => setCourierPage((current) => Math.min(totalCourierPages, current + 1))}
                  >
                    Berikutnya
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Payment */}
          <div className="bg-white border border-stone-200 rounded-2xl p-6 sm:p-8 shadow-sm">
            <div className="flex items-center gap-3 mb-5">
              <span className="inline-flex w-10 h-10 rounded-xl bg-purple-50 text-purple-700 items-center justify-center">
                <CreditCard className="w-5 h-5" />
              </span>
              <div>
                <h2 className="font-display text-xl sm:text-2xl font-semibold text-stone-900">
                  Metode Pembayaran
                </h2>
                <p className="text-stone-500 text-sm">
                  Pilih metode yang paling nyaman untuk Anda.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-2.5">
              {activePaymentMethods.map((p) => {
                const active = selectedPaymentId === p.code;
                return (
                  <button
                    key={p.code}
                    type="button"
                    onClick={() => setSelectedPaymentId(p.code)}
                    className={`text-left p-4 rounded-xl border flex items-center justify-between gap-3 transition ${
                      active
                        ? "border-emerald-500 bg-emerald-50 ring-2 ring-emerald-200"
                        : "border-stone-200 bg-white hover:border-stone-300"
                    }`}
                    data-testid={`payment-option-${p.code}`}
                  >
                    <span>
                      <span className="block font-medium text-stone-900">{p.name}</span>
                      {p.description && (
                        <span className="block text-sm text-stone-500">{p.description}</span>
                      )}
                    </span>
                    <span
                      className={`w-5 h-5 rounded-full border-2 ${
                        active
                          ? "border-emerald-600 bg-emerald-600"
                          : "border-stone-300 bg-white"
                      }`}
                    ></span>
                  </button>
                );
              })}
              {activePaymentMethods.length === 0 && (
                <div className="rounded-xl border border-dashed border-stone-300 p-5 text-stone-500">
                  Belum ada metode bayar aktif.
                </div>
              )}
            </div>
          </div>

          {/* Mobile summary */}
          <div className="lg:hidden">
            <CheckoutSummary />
          </div>

          <Button
            onClick={onCreate}
            disabled={!selectedCourierId || !selectedPaymentId || creating}
            className="w-full h-16 rounded-xl text-lg font-medium bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm disabled:bg-stone-200 disabled:text-stone-400"
            data-testid="create-order-button"
          >
            {creating ? "Membuat Transaksi..." : "Buat Pesanan"}
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>

        <div className="hidden lg:block">
          <div className="sticky top-24">
            <CheckoutSummary />
          </div>
        </div>
      </div>
    </main>
  );
};

export default CheckoutPaymentPage;


